import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup, onAuthStateChanged, User } from 'firebase/auth';
import { 
  initializeFirestore, 
  memoryLocalCache,
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  onSnapshot, 
  query, 
  where, 
  deleteDoc, 
  serverTimestamp, 
  getDocFromServer, 
  addDoc, 
  orderBy, 
  limit 
} from 'firebase/firestore';
import firebaseConfig from '../firebase-applet-config.json';

// Initialize Firebase SDK
const app = initializeApp(firebaseConfig);

// Using initializeFirestore with specialized settings to bypass connectivity issues
// and the BloomFilter: Invalid hash count: 0 error in JS SDK v12.12.0
export const db = initializeFirestore(app, {
  localCache: memoryLocalCache(), // Use memory cache to avoid persistence issues in sandboxed environments
  experimentalForceLongPolling: true, // Force long polling to bypass WebSocket restrictions
}, firebaseConfig.firestoreDatabaseId);

export const auth = getAuth(app);
export const googleProvider = new GoogleAuthProvider();

// Error Handling Types
export const OperationType = {
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
  LIST: 'list',
  GET: 'get',
  WRITE: 'write',
} as const;

export type OperationType = typeof OperationType[keyof typeof OperationType];

export interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId: string | undefined;
    email: string | null | undefined;
    emailVerified: boolean | undefined;
    isAnonymous: boolean | undefined;
    tenantId: string | null | undefined;
    providerInfo: {
      providerId: string;
      displayName: string | null;
      email: string | null;
      photoUrl: string | null;
    }[];
  }
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData.map(provider => ({
        providerId: provider.providerId,
        displayName: provider.displayName,
        email: provider.email,
        photoUrl: provider.photoURL
      })) || []
    },
    operationType,
    path
  };
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

// Connection Test
async function testConnection() {
  try {
    await getDocFromServer(doc(db, 'test', 'connection'));
    (window as any).LATTICE_CONNECTED = true;
  } catch (error) {
    (window as any).LATTICE_CONNECTED = false;
    if(error instanceof Error && (error.message.includes('the client is offline') || error.message.includes('Could not reach Cloud Firestore'))) {
      console.error("Lattice Connectivity Error: ", error.message);
    }
  }
}

// Manual Re-grounding Logic
async function reground() {
  console.log("[Lattice] Re-grounding protocol initiated...");
  try {
    await testConnection();
    if ((window as any).LATTICE_CONNECTED) {
      console.log("[Lattice] Node re-anchored successfully.");
    } else {
      console.warn("[Lattice] Grounding unsuccessful. Check gateway.");
    }
  } catch (e) {
    console.error("[Lattice] Re-grounding failure:", e);
  }
}

(window as any).REGROUND_LATTICE = reground;

testConnection();
// Re-check periodically
setInterval(testConnection, 30000);

export { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  onSnapshot, 
  query, 
  where, 
  deleteDoc, 
  serverTimestamp, 
  signInWithPopup, 
  onAuthStateChanged,
  addDoc,
  orderBy,
  limit
};
export type { User };
